import { t } from 'i18next';
import { useNavigate } from 'react-router';

import AccessoryHeader from 'components/desktop/Accessory/AccessoryHeader/AccessoryHeader';
import * as AccessoryState from 'components/desktop/Accessory/AccessoryState';
import TabGeneral from 'components/desktop/Accessory/Tabs/TabGeneral';
import TabMetadata from 'components/desktop/Accessory/Tabs/TabMetadata';
import { AccessoryDetailData } from 'components/desktop/Accessory/useAccessoryData';
import useAccessoryData from 'components/desktop/Accessory/useAccessoryData';
import { AccessoryDetailHandlers } from 'components/desktop/Accessory/useAccessoryState';
import useAccessoryState from 'components/desktop/Accessory/useAccessoryState';
import LoadingAnimation from 'components/desktop/_general/Loading/LoadingAnimation';
import Tabs, { Tab } from 'components/desktop/_general/Tabs/Tabs';
import { Optional } from 'functions/promiseExtensions';
import useCultureCode from 'hooks/redux/useCultureCode';
import useAirBlocker from 'hooks/useAirBlocker';
import { useIsHandlerLoaded } from 'hooks/useIsLoading';
import { usePermissions } from 'hooks/usePermissions';
import * as Accessory from 'types/Accessory/Detail/Accessory';

interface IProps {
	id?: string;
	initialState: Accessory.Type;
	isLoading: boolean;
}

const AccessoryComponent = (props: IProps) => {
	const cultureCode: string = useCultureCode();
	const { isAccessoryEditor } = usePermissions();
	const data = useAccessoryData();

	const { state, handlers } = useAccessoryState(
		Accessory.mapToState(props.initialState, cultureCode)
	);

	const putAccessory = async (): Promise<void> => {
		const newState = await AccessoryState.save(state, cultureCode);

		handlers.load(newState);
		await updateCache(newState);
	};

	const postAccessory = async (): Promise<void> => {
		const newState = await AccessoryState.save(state, cultureCode);
		if (newState.id) {
			await updateCache(state);
			navigate(`/accessory/detail/${newState.id}`);
		}
	};

	const [isLoaded, handleSave] = useIsHandlerLoaded(
		Optional.WrapToJust(state, state.id ? putAccessory : postAccessory)
	);

	const { updateCache } = useAirBlocker(state, handleSave);
	const navigate = useNavigate();

	const tabs: Tab[] = [
		{
			name: t('_general:GENERAL'),
			content: <TabGeneral state={state} handlers={handlers} data={data} />,
		},
		{
			name: t('_general:METADATA'),
			content: <TabMetadata state={state} handlers={handlers} data={data} />,
		},
	];

	return (
		<>
			<LoadingAnimation isLoading={!isLoaded || props.isLoading} />
			<AccessoryHeader
				allTags={data.tags}
				cultureCode="de-DE"
				handleSave={() => handleSave()}
				id={state.id}
				image={state.image}
				isLoading={!isLoaded || props.isLoading}
				readOnly={!isAccessoryEditor}
				state={state}
			/>
			<br />
			<Tabs
				tabs={tabs.filter((t: Tab) => {
					if (t.display === undefined || t.display === true) {
						return t;
					}
				})}
			/>
		</>
	);
};

export default AccessoryComponent;

export interface AccessoryTabProps {
	state: AccessoryState.Type;
	handlers: AccessoryDetailHandlers;
	data: AccessoryDetailData;
}
